/**
 * LD Cart Drawer - Independent cart drawer for LD theme
 * This provides full control over cart operations without conflicts with the main theme
 */

console.log('LD Cart Drawer: File loaded');
console.log('=== LD CART DRAWER LOADED ===');

class LDCartDrawer {
  constructor() {
    this.drawer = null;
    this.isOpen = false;
    this.isUpdating = false;
    
    // Initialize modules using global classes
    this.operations = null;
    this.crossSell = null;
    this.ui = null;
    
    this.init();
  }

  init() {
    console.log('LD Cart Drawer: Initializing...');
    
    // Wait a bit for DOM to be fully ready
    setTimeout(() => {
      // Find the cart drawer element
      this.drawer = document.getElementById('ld-cart-drawer');
      if (!this.drawer) {
        console.error('LD Cart Drawer: Drawer element not found');
        return;
      }
      
      if (this.drawer) {
        console.log('LD Cart Drawer: Drawer element found:', this.drawer);
        
        // Initialize modules using global classes
        this.operations = new window.LDCartOperations(this);
        this.crossSell = new window.LDCartCrossSell(this);
        this.ui = new window.LDCartUI(this);
        
        // Set up event listeners
        this.ui.setupEventListeners();
        
        // Set up cart update listeners
        this.setupCartListeners();
        
        // Cross-sell is static; no filtering required
        
        console.log('LD Cart Drawer: Initialized successfully');
      }
    }, 100);
  }





  setupCartListeners() {
    // Listen for cart updates from our product page
    document.addEventListener('ld:cart:updated', (event) => {
      console.log('LD Cart Drawer: Cart updated event received:', event.detail);
      this.refreshCart();
    });

    // Listen for cart changes from other parts of the theme
    document.addEventListener('cart:change', (event) => {
      console.log('LD Cart Drawer: Theme cart change event received:', event.detail);
      this.refreshCart();
    });
  }



  async refreshCart() {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    console.log('LD Cart Drawer: Refreshing cart...');
    
    try {
      // Get updated cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      // Get updated cart HTML with section settings
      const sectionsResponse = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=ld-cart-drawer&cart_token=${cartData.token || ''}&timestamp=${Date.now()}`);
      const sectionsHtml = await sectionsResponse.text();
      
      // Parse the HTML to extract the cart drawer section
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionsHtml, 'text/html');
      const newCartDrawer = doc.querySelector('.ld-cart-drawer');
      
      if (newCartDrawer) {
        // Preserve current cross-sell DOM to keep it static and visible
        const existingCrossSell = this.drawer.querySelector('.ld-cart-drawer__additional-products');
        const preservedCrossSellHTML = existingCrossSell ? existingCrossSell.outerHTML : '';
        // Store the current scroll position
        const scrollTop = this.drawer.querySelector('.ld-cart-drawer__body')?.scrollTop || 0;
        
        // Store current drawer classes before updating content
        const currentClasses = this.drawer.className;
        
        // Update entire drawer content to ensure cross-sell section is properly rendered
        this.drawer.innerHTML = newCartDrawer.innerHTML;
        
        // Restore preserved cross-sell DOM
        if (preservedCrossSellHTML) {
          const newCrossSell = this.drawer.querySelector('.ld-cart-drawer__additional-products');
          if (newCrossSell) {
            newCrossSell.outerHTML = preservedCrossSellHTML;
          }
        }
        
        // Restore classes and update based on cart state
        this.drawer.className = currentClasses;
        
        // Update drawer classes based on cart state
        if (cartData.item_count === 0) {
          this.drawer.classList.add('drawer--center-body');
        } else {
          this.drawer.classList.remove('drawer--center-body');
        }
        
        // Re-setup event listeners for new content
        this.ui.setupEventListeners();
        
        // Update cart count indicators
        this.ui.updateCartCount(cartData.item_count);
        // Log cross-sell items count
        const crossSellItemsAfterRefresh = this.drawer.querySelectorAll('.ld-cart-drawer__additional-item');
        console.log('LD Cart Drawer: Cross-sell items after refresh (static):', crossSellItemsAfterRefresh.length);
        
        // Restore scroll position
        const bodyElement = this.drawer.querySelector('.ld-cart-drawer__body');
        if (bodyElement) {
          bodyElement.scrollTop = scrollTop;
        }
        
        console.log('LD Cart Drawer: Cart refreshed successfully');
      } else {
        console.error('LD Cart Drawer: Could not find cart drawer section in response');
      }
    } catch (error) {
      console.error('LD Cart Drawer: Error refreshing cart:', error);
    } finally {
      this.isUpdating = false;
    }
  }





  // Filter and show/hide cross-sell products based on cart contents
  async filterCrossSellProducts() { /* no-op: cross-sell is static */ }

  // Public method to add item to cart and show drawer
  async addToCartAndShow(variantId, quantity = 1, properties = {}) {
    console.log(`LD Cart Drawer: Adding variant ${variantId} to cart with quantity ${quantity}`);
    
    try {
      // Check if this variant is already in cart
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      const existingItem = cartData.items.find(item => item.variant_id.toString() === variantId.toString());
      if (existingItem) {
        console.log(`LD Cart Drawer: Variant ${variantId} already in cart, quantity: ${existingItem.quantity}`);
        
        // Don't update quantity if it's already correct
        if (existingItem.quantity === quantity) {
          console.log('LD Cart Drawer: Quantity already correct, just showing drawer');
          await this.refreshCart();
          this.ui.show();
          return existingItem;
        }
        
        // Update quantity only if it's different
        console.log(`LD Cart Drawer: Updating quantity from ${existingItem.quantity} to ${quantity}`);
        await this.operations.setQuantity(existingItem.id, quantity);
        
        // Refresh cart to show updated content, then show drawer
        await this.refreshCart();
        this.ui.show();
        return existingItem;
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: properties
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('LD Cart Drawer: Item added to cart successfully:', result);
        
        // First refresh cart to get updated content
        await this.refreshCart();
        
        // Filter cross-sell products
        await this.filterCrossSellProducts();
        
        // Then show drawer with updated content
        this.ui.show();
        
        // Dispatch success event
        document.dispatchEvent(new CustomEvent('ld:cart:item:added', {
          bubbles: true,
          detail: { variantId, quantity, properties, result }
        }));
        
        return result;
      } else {
        const error = await response.json();
        console.error('LD Cart Drawer: Failed to add item to cart:', error);
        throw new Error(error.description || 'Failed to add item to cart');
      }
    } catch (error) {
      console.error('LD Cart Drawer: Error adding item to cart:', error);
      throw error;
    }
  }

  // Public show method for compatibility with main theme
  async show() {
    if (this.ui) {
      this.ui.show();
    }
  }

}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.ldCartDrawer = new LDCartDrawer();
});

// Make class globally available
window.LDCartDrawer = LDCartDrawer;
